import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import GoldShieldLogo from "@/components/GoldShieldLogo";
import SEO from "@/components/SEO";
import { trackSiteVisit } from "@/lib/api";
import { 
  Truck, 
  Clock, 
  Shield,
  ChevronRight,
  Check,
  Factory,
  Car,
  FileText,
  Speaker,
  Cog,
  Package,
  Zap,
  Timer,
  Target,
  CreditCard,
  Phone,
  Star,
  MessageCircle
} from "lucide-react";

/* WhatsApp Floating Widget Component */
function WhatsAppWidget() {
  const [isExpanded, setIsExpanded] = useState(false);
  const whatsappNumber = "447466088858";
  const message = encodeURIComponent("Hello RTS EXPRESS, I would like to make an enquiry about dedicated transport.");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

  const handleWhatsAppClick = () => {
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-end gap-3">
      {/* Expanded Label */}
      {isExpanded && (
        <div className="bg-white rounded-xl shadow-2xl p-4 mb-2 animate-in slide-in-from-right-5 duration-300">
          <p className="text-slate-800 font-medium text-sm">Chat with us on WhatsApp</p>
          <p className="text-slate-500 text-xs mt-1">Get quick answers 24/7</p>
        </div>
      )}
      
      {/* WhatsApp Button */}
      <button
        onClick={handleWhatsAppClick}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        className="flex items-center justify-center w-14 h-14 bg-[#25D366] hover:bg-[#20bd5a] rounded-full shadow-lg shadow-[#25D366]/30 hover:shadow-xl hover:shadow-[#25D366]/40 transition-all duration-300 hover:scale-105"
        aria-label="Chat on WhatsApp"
        type="button"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </button>
    </div>
  );
}

/* Review Cards Component - Positioned in hero area */
function TrustCards() {
  const handleGoogleReviewsClick = () => {
    window.open('https://g.page/r/CZ1ugqJwbQFiEBM/review', '_blank', 'noopener,noreferrer');
  };

  const handleTrustpilotClick = () => {
    window.open('https://www.trustpilot.com/evaluate/rtsexpress.co.uk', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Google Reviews Card */}
      <button
        onClick={handleGoogleReviewsClick}
        className="flex items-center gap-3 bg-[#1a1f2e]/90 hover:bg-[#1a1f2e] border border-white/10 hover:border-white/20 rounded-xl px-5 py-4 transition-all duration-300 group backdrop-blur-md cursor-pointer text-left min-w-[200px]"
        type="button"
        aria-label="Read our Google Reviews"
      >
        {/* Google G Logo */}
        <div className="flex-shrink-0">
          <svg className="w-8 h-8" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold text-sm mb-1">Google Reviews</p>
          <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-[#FBBC05] text-[#FBBC05]" />
            ))}
          </div>
          <p className="text-slate-400 text-xs mt-1 group-hover:text-[#22c55e] transition-colors">Read our reviews →</p>
        </div>
      </button>

      {/* Trustpilot Card */}
      <button
        onClick={handleTrustpilotClick}
        className="flex items-center gap-3 bg-[#1a1f2e]/90 hover:bg-[#1a1f2e] border border-white/10 hover:border-white/20 rounded-xl px-5 py-4 transition-all duration-300 group backdrop-blur-md cursor-pointer text-left min-w-[200px]"
        type="button"
        aria-label="Read our Trustpilot reviews"
      >
        {/* Trustpilot Star Logo */}
        <div className="flex-shrink-0">
          <svg className="w-8 h-8" viewBox="0 0 24 24">
            <path fill="#00B67A" d="M12 0L15.708 7.512L24 8.702L18 14.527L19.416 22.8L12 18.926L4.584 22.8L6 14.527L0 8.702L8.292 7.512L12 0Z"/>
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold text-sm mb-1">Trustpilot</p>
          <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-[#00B67A] text-[#00B67A]" />
            ))}
          </div>
          <p className="text-slate-400 text-xs mt-1 group-hover:text-[#22c55e] transition-colors">Read our reviews →</p>
        </div>
      </button>
    </div>
  );
}

export default function HomePage() {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  // Track site visit on page load
  useEffect(() => {
    trackSiteVisit();
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Programmatic navigation handlers (no URL shown in status bar)
  const handleQuoteClick = () => {
    navigate('/get-quote');
  };

  const handleExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handlePhoneCall = () => {
    window.location.href = 'tel:+442477102568';
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/447466088858?text=Hello RTS EXPRESS%2C I would like to make an enquiry about dedicated transport.', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-[#0B0F14]">
      <SEO 
        title="RTS EXPRESS LTD - Same Day Courier UK | Dedicated Transport"
        description="UK's trusted same-day courier service. Dedicated 3.5T van transport for businesses. Emergency delivery across England, Scotland & Wales. Get instant quotes."
        path="/"
      />
      {/* Navigation - Premium Clean Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F14]/98 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-[72px]">
            {/* Logo - Left */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <GoldShieldLogo className="w-9 h-11" />
              <div className="flex flex-col">
                <span className="font-bold text-white text-lg tracking-wide leading-tight">
                  RTS EXPRESS
                </span>
                <span className="text-[9px] text-slate-400 tracking-[0.2em] uppercase">Driven by Delivery</span>
              </div>
            </Link>
            
            {/* Desktop Navigation - Center */}
            <div className="hidden xl:flex items-center justify-center flex-1 px-8">
              <div className="flex items-center gap-6">
                <button onClick={() => scrollToSection('dedicated-transport')} className="text-[13px] text-slate-300 hover:text-white transition-colors whitespace-nowrap">
                  Dedicated<br/>Transport
                </button>
                <button onClick={() => scrollToSection('industries')} className="text-[13px] text-slate-300 hover:text-white transition-colors">Industries</button>
                <button onClick={() => scrollToSection('premium-tiers')} className="text-[13px] text-slate-300 hover:text-white transition-colors whitespace-nowrap">
                  Premium<br/>Response Tiers
                </button>
                <button onClick={() => scrollToSection('how-it-works')} className="text-[13px] text-slate-300 hover:text-white transition-colors whitespace-nowrap">How It Works</button>
                <button onClick={() => scrollToSection('what-we-deliver')} className="text-[13px] text-slate-300 hover:text-white transition-colors whitespace-nowrap">What We Deliver</button>
                <button onClick={() => scrollToSection('contact')} className="text-[13px] text-slate-300 hover:text-white transition-colors">Contact</button>
                <Link to="/get-quote" className="text-[13px] text-slate-300 hover:text-white transition-colors">Quote</Link>
                <Link to="/blog" className="text-[13px] text-slate-300 hover:text-white transition-colors">Blog</Link>
              </div>
            </div>
            
            {/* Phone & CTA - Right */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Phone Contact - Desktop: Plain text, selectable, no click action */}
              <div className="hidden lg:flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40">
                  <Phone className="w-5 h-5 text-[#22c55e]" />
                </div>
                <div className="hidden xl:flex flex-col select-text">
                  <span className="text-[10px] text-white leading-tight">Need Assistance?</span>
                  <span className="text-white font-semibold text-sm leading-tight select-all cursor-text">+44 2477 102 568</span>
                  <span className="text-[9px] text-white leading-tight">We're here 24/7</span>
                </div>
              </div>
              
              <Button 
                onClick={handleQuoteClick}
                className="bg-[#22c55e] text-black hover:bg-[#1ea550] font-semibold px-5 py-2.5 text-sm rounded-md transition-all border-0"
                data-testid="nav-get-quote-btn"
                type="button"
              >
                Request Quote
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Premium Layout with RTS Express Photo */}
      <section id="dedicated-transport" className="relative min-h-screen flex items-center pt-[72px] overflow-hidden">
        {/* Background Image - Responsive: Desktop and Mobile versions */}
        {/* Desktop Hero Image - visible on lg screens and up */}
        <div 
          className="absolute inset-0 hidden lg:block"
          style={{
            backgroundImage: `url('https://customer-assets-v7afamib.emergentagent.net/job_066e4793-a057-4cc2-aaee-882a3ace6ca9/artifacts/ld28gpbg_WhatsApp%20Image%202026-09-28%20at%2012.14.22%20%281%29.jpeg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        {/* Mobile Hero Image - visible on screens smaller than lg */}
        <div 
          className="absolute inset-0 lg:hidden"
          style={{
            backgroundImage: `url('https://customer-assets-v7afamib.emergentagent.net/job_066e4793-a057-4cc2-aaee-882a3ace6ca9/artifacts/73sjehw4_WhatsApp%20Image%202026-09-28%20at%2012.14.22.jpeg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 20%',
            backgroundRepeat: 'no-repeat'
          }}
        />
        {/* Gradient Overlay - Strong on left for text, transparent on right for photo */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#080c10]/90 via-[#080c10]/50 via-40% to-transparent" />
        
        {/* Hero Content - Left side only */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10">
          <div className="max-w-xl">
            {/* Mobile Phone Number - Above badge, visible only on mobile */}
            <a 
              href="tel:+442477102568"
              className="lg:hidden flex items-center gap-3 mb-4 group"
              aria-label="Tap to call +44 2477 102 568"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/20 group-active:bg-white/20 transition-colors">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-white font-bold text-xl tracking-wide">+44 2477 102 568</span>
                <span className="text-slate-300 text-xs">Tap to call</span>
              </div>
              <ChevronRight className="w-5 h-5 text-white/60 ml-1" />
            </a>

            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 bg-[#22c55e] rounded-full px-5 py-2.5 mb-8">
              <Check className="w-4 h-4 text-black" />
              <span className="text-black text-sm font-semibold">24/7 Emergency Courier Service</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Dedicated{" "}
              <span className="text-[#22c55e]">Time-Critical</span>
              <br />
              Transport Across the UK
            </h1>

            <p className="text-slate-300 text-lg lg:text-xl max-w-md mb-8 leading-relaxed">
              Direct vehicle deployment. No consolidation. No unnecessary stops.
            </p>

            {/* Bullet Points */}
            <ul className="space-y-3 mb-10">
              {[
                "Dedicated vehicle only",
                "Same-day nationwide coverage",
                "Fully insured goods in transit",
                "24/7 availability"
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3 text-slate-200">
                  <Check className="w-5 h-5 text-[#22c55e] flex-shrink-0" />
                  <span className="text-base">{item}</span>
                </li>
              ))}
            </ul>

            {/* CTA Buttons + Review Cards - ALL 4 ON SAME HORIZONTAL LINE WITH EQUAL WIDTH */}
            <div className="hidden lg:flex items-stretch gap-3">
              {/* 1. Request Dedicated Transport Button */}
              <Button 
                onClick={handleQuoteClick}
                className="flex-1 basis-0 bg-[#22c55e] text-black hover:bg-[#1ea550] font-bold text-sm px-4 py-4 h-[60px] rounded-md transition-all border-0 whitespace-nowrap flex items-center justify-center"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                data-testid="hero-get-quote-btn"
                type="button"
              >
                Request Dedicated Transport
                <ChevronRight className={`ml-1 w-4 h-4 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} />
              </Button>
              
              {/* 2. Learn More Button */}
              <Button 
                variant="outline"
                onClick={() => scrollToSection('industries')}
                className="flex-1 basis-0 border-slate-600 text-white hover:bg-white/10 hover:border-slate-500 font-semibold text-sm px-4 py-4 h-[60px] rounded-md bg-slate-800/80 whitespace-nowrap flex items-center justify-center"
              >
                Learn More
              </Button>
              
              {/* 3. Google Reviews Card - Equal size */}
              <button
                onClick={() => window.open('https://g.page/r/CZ1ugqJwbQFiEBM/review', '_blank', 'noopener,noreferrer')}
                className="flex-1 basis-0 flex items-center justify-center gap-2 bg-[#1a1f2e]/90 hover:bg-[#1a1f2e] border border-white/10 hover:border-white/20 rounded-md px-4 py-4 h-[60px] transition-all group backdrop-blur-md cursor-pointer"
                type="button"
                aria-label="Read our Google Reviews"
              >
                <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <div className="text-left">
                  <p className="text-white font-semibold text-sm leading-tight">Google Reviews</p>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-[#FBBC05] text-[#FBBC05]" />
                    ))}
                  </div>
                </div>
              </button>
              
              {/* 4. Trustpilot Card - Equal size */}
              <button
                onClick={() => window.open('https://www.trustpilot.com/evaluate/rtsexpress.co.uk', '_blank', 'noopener,noreferrer')}
                className="flex-1 basis-0 flex items-center justify-center gap-2 bg-[#1a1f2e]/90 hover:bg-[#1a1f2e] border border-white/10 hover:border-white/20 rounded-md px-4 py-4 h-[60px] transition-all group backdrop-blur-md cursor-pointer"
                type="button"
                aria-label="Read our Trustpilot reviews"
              >
                <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="#00B67A" d="M12 0L15.708 7.512L24 8.702L18 14.527L19.416 22.8L12 18.926L4.584 22.8L6 14.527L0 8.702L8.292 7.512L12 0Z"/>
                </svg>
                <div className="text-left">
                  <p className="text-white font-semibold text-sm leading-tight">Trustpilot</p>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-[#00B67A] text-[#00B67A]" />
                    ))}
                  </div>
                </div>
              </button>
            </div>
            
            {/* Mobile version - stacked for smaller screens */}
            <div className="flex lg:hidden flex-col gap-3">
              <div className="flex gap-3">
                <Button 
                  onClick={handleQuoteClick}
                  className="flex-1 bg-[#22c55e] text-black hover:bg-[#1ea550] font-bold text-sm px-4 py-4 h-auto rounded-md transition-all border-0"
                  data-testid="hero-get-quote-btn-mobile"
                  type="button"
                >
                  Request Transport
                  <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => scrollToSection('industries')}
                  className="border-slate-600 text-white hover:bg-white/10 font-semibold text-sm px-4 py-4 h-auto rounded-md bg-slate-800/80"
                >
                  Learn More
                </Button>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => window.open('https://g.page/r/CZ1ugqJwbQFiEBM/review', '_blank', 'noopener,noreferrer')}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#1a1f2e]/90 border border-white/10 rounded-md px-3 py-3"
                  type="button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="text-white text-xs font-medium">Google</span>
                </button>
                <button
                  onClick={() => window.open('https://www.trustpilot.com/evaluate/rtsexpress.co.uk', '_blank', 'noopener,noreferrer')}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#1a1f2e]/90 border border-white/10 rounded-md px-3 py-3"
                  type="button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#00B67A" d="M12 0L15.708 7.512L24 8.702L18 14.527L19.416 22.8L12 18.926L4.584 22.8L6 14.527L0 8.702L8.292 7.512L12 0Z"/>
                  </svg>
                  <span className="text-white text-xs font-medium">Trustpilot</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* WhatsApp Floating Widget */}
      <WhatsAppWidget />

      {/* Industries Section */}
      <section id="industries" className="relative py-24 px-6 lg:px-8 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/200aa50b-2028-488c-bd6e-ea40d2652c3c/images/9469a9e42b69c802cbf5b166b5bea379a2adab346da4213edd943964d915e4a1.png')`
          }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/95 via-[#0B0F14]/85 to-[#0B0F14]/60" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header with Logo */}
          <div className="flex items-center gap-3 mb-8">
            <GoldShieldLogo className="w-8 h-10" />
            <span className="font-bold text-white text-lg tracking-wide">RTS EXPRESS</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                Industries We Support:
              </h2>
              <p className="text-slate-300 text-lg mb-10">
                Tailored time-critical dispatch options to match urgency of your business needs.
              </p>

              {/* Industry Cards - Glass Effect */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { icon: Factory, title: "Manufacturing & Production" },
                  { icon: Car, title: "Automotive Engineering" },
                  { icon: FileText, title: "Legal & Confidential Documents" },
                  { icon: Speaker, title: "Events & Technical Equipment" },
                  { icon: Cog, title: "Industrial Parts & Components" },
                  { icon: Package, title: "High-Value Commercial Shipments" },
                ].map((industry, index) => (
                  <div 
                    key={index} 
                    className="group bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-5 hover:bg-white/10 hover:border-[#22c55e]/30 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-10 h-10 border border-[#22c55e]/50 rounded-lg flex items-center justify-center mb-3 group-hover:border-[#22c55e] transition-colors">
                      <industry.icon className="w-5 h-5 text-[#22c55e]" />
                    </div>
                    <h3 className="text-white text-sm font-medium leading-tight">{industry.title}</h3>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side empty for image visibility */}
            <div className="hidden lg:block" />
          </div>
        </div>
      </section>

      {/* Premium Response Tiers Section */}
      <section id="premium-tiers" className="relative py-24 px-6 lg:px-8 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/200aa50b-2028-488c-bd6e-ea40d2652c3c/images/006bf8bb874397f9296560969368541bea66b21de27db5bc3bc4c95db177aaab.png')`
          }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/95 via-[#0B0F14]/85 to-[#0B0F14]/50" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header with Logo */}
          <div className="flex items-center gap-3 mb-8">
            <GoldShieldLogo className="w-8 h-10" />
            <span className="font-bold text-white text-lg tracking-wide">RTS EXPRESS</span>
          </div>

          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Premium Response Tiers
            </h2>
            <p className="text-slate-300 text-lg mb-10">
              Tailored time-critical dispatch options to match the urgency of your business needs.
            </p>

            {/* Response Tier Cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              {[
                { icon: Truck, title: "Executive Dedicated Same-Day", desc: "Direct vehicle allocation. Exclusive transport." },
                { icon: Timer, title: "Priority Commercial Deployment", desc: "Accelerated mobilisation for time-sensitive consignments." },
                { icon: Zap, title: "Critical Executive Response", desc: "Immediate deployment for mission-critical consignments." },
              ].map((tier, index) => (
                <div 
                  key={index} 
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-5 hover:bg-white/10 hover:border-[#22c55e]/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-xl flex items-center justify-center mb-4">
                    <tier.icon className="w-6 h-6 text-[#22c55e]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{tier.title}</h3>
                  <p className="text-slate-400 text-sm">{tier.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/get-quote">
                <Button 
                  className="bg-[#22c55e] text-black hover:bg-[#1ea550] font-bold text-base px-8 py-6 h-auto rounded-md transition-all border-0"
                  data-testid="premium-tiers-cta"
                >
                  Request Transport Now
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button 
                variant="outline"
                onClick={() => scrollToSection('how-it-works')}
                className="border-slate-600 text-white hover:bg-white/10 hover:border-slate-500 font-semibold text-base px-8 py-6 h-auto rounded-md bg-slate-800/50"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6 lg:px-8 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#22c55e] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">Simple Process</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Four simple steps from request to delivery. No complexity, no delays.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Request Quote", desc: "Submit your delivery details through our quick online form", icon: FileText },
              { step: "02", title: "Receive Pricing", desc: "Get a competitive quote delivered straight to your inbox", icon: CreditCard },
              { step: "03", title: "Confirm & Pay", desc: "Secure payment via Stripe. Your driver is assigned immediately", icon: Check },
              { step: "04", title: "We Deliver", desc: "Direct collection and delivery. No hub stops, no transfers", icon: Truck },
            ].map((item, index) => (
              <div key={index} className="relative group text-center">
                <div className="w-16 h-16 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#22c55e]/20 transition-colors">
                  <item.icon className="w-8 h-8 text-[#22c55e]" />
                </div>
                <span className="text-[#22c55e] text-sm font-bold mb-2 block">Step {item.step}</span>
                <h3 className="text-white font-bold text-lg mb-3">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/get-quote">
              <Button 
                className="bg-[#22c55e] text-black hover:bg-[#1ea550] font-bold text-base px-10 py-6 h-auto rounded-md transition-all border-0"
                data-testid="how-it-works-cta"
              >
                Start Your Quote
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What We Deliver Section */}
      <section id="what-we-deliver" className="py-24 px-6 lg:px-8 bg-[#080808]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#22c55e] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">Our Services</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              What We Deliver
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              From time-sensitive documents to heavy cargo — your consignment travels direct, in a dedicated vehicle.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: FileText, title: "Documents", desc: "Urgent contracts, legal files, and time-sensitive paperwork. Handled with care, delivered with speed.", color: "#22c55e" },
              { icon: Package, title: "Parcels", desc: "From small boxes to multiple cartons. Secure handling for your valuable goods, any size.", color: "#3b82f6" },
              { icon: Truck, title: "Pallets", desc: "Up to 2 pallets per trip. 3.5T van capacity within UK legal weight limits (max 1200kg).", color: "#f59e0b" },
            ].map((service, index) => (
              <div 
                key={index} 
                className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${service.color}15`, border: `1px solid ${service.color}30` }}
                >
                  <service.icon className="w-7 h-7" style={{ color: service.color }} />
                </div>
                <h3 className="text-white font-bold text-xl mb-3">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 px-6 lg:px-8 bg-[#0B0F14] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { icon: Shield, title: "VAT Registered", desc: "GB414935787" },
              { icon: Shield, title: "Fully Insured", desc: "Goods in Transit" },
              { icon: Truck, title: "Dedicated Vehicle", desc: "Direct Delivery" },
              { icon: CreditCard, title: "Secure Payment", desc: "Stripe Protected" },
              { icon: Clock, title: "24/7 Available", desc: "Including Holidays" },
            ].map((badge, index) => (
              <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                <div className="w-10 h-10 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <badge.icon className="w-5 h-5 text-[#22c55e]" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1">{badge.title}</h3>
                <p className="text-slate-500 text-xs">{badge.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Conditions */}
      <section className="py-16 px-6 lg:px-8 bg-[#080C12]">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/5 border border-white/10 rounded-xl p-8">
            <h3 className="text-white font-bold text-xl mb-6">Service Conditions</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
              {[
                { text: "Maximum 1-2 pallets per trip", highlight: false },
                { text: "UK legal weight limits apply (1200kg max)", highlight: false },
                { text: "No ADR / hazardous goods", highlight: false },
                { text: "No illegal goods transported", highlight: false },
                { text: "Payment required before collection", highlight: true },
                { text: "Based in Nuneaton, Warwickshire", highlight: false },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-400">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${item.highlight ? 'bg-amber-500' : 'bg-[#22c55e]'}`} />
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Premium Dark */}
      <footer id="contact" className="py-16 px-6 lg:px-8 bg-[#050810] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <GoldShieldLogo className="w-8 h-10" />
                <div>
                  <span className="font-bold text-white block leading-tight">RTS EXPRESS</span>
                  <span className="text-[10px] text-slate-500 tracking-[0.15em] uppercase">Driven by Delivery</span>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Premium same-day courier services across the United Kingdom. When time is critical, we deliver.
              </p>
            </div>

            {/* Company Information */}
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Company Information</h4>
              <p className="text-slate-400 text-sm font-medium">RTS EXPRESS LTD</p>
              <p className="text-slate-500 text-xs mt-2 italic">Registered in England and Wales</p>
              <p className="text-slate-500 text-sm mt-3">Company No: 13973121</p>
              <p className="text-slate-500 text-sm">VAT No: GB414935787</p>
              <p className="text-slate-500 text-sm">ICO: ZC095659</p>
            </div>

            {/* Registered Office */}
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Registered Office</h4>
              <p className="text-slate-400 text-sm leading-relaxed">
                Nuneaton<br />
                Warwickshire<br />
                United Kingdom
              </p>
            </div>

            {/* CTA */}
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Ready to Ship?</h4>
              <p className="text-slate-400 text-sm mb-4">24/7 availability including bank holidays</p>
              <Link to="/get-quote">
                <Button className="bg-[#22c55e] text-black hover:bg-[#1ea550] font-semibold text-sm w-full rounded-md border-0">
                  Request Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/5">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
              <p className="text-slate-500 text-sm" data-testid="footer-copyright">
                © {new Date().getFullYear()} RTS EXPRESS LTD. All rights reserved.
              </p>
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <span>Company No: 13973121</span>
                <span className="text-white/10">|</span>
                <span>VAT No: GB414935787</span>
                <span className="text-white/10">|</span>
                <span>ICO: ZC095659</span>
              </div>
            </div>
            
            <p className="mt-4 text-slate-500 text-sm" data-testid="footer-email">
              info@rtsexpress.co.uk
            </p>
            <p className="mt-1 text-slate-500 text-sm">
              gdpr@rtsexpress.co.uk
            </p>
            <p className="mt-1 text-slate-500 text-sm">
              quotes@rtsexpress.co.uk
            </p>
            <p className="mt-1 text-slate-500 text-sm">
              recruitment@rtsexpress.co.uk
            </p>
            
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <Link to="/terms-and-conditions" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-terms-link">
                Terms &amp; Conditions
              </Link>
              <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-cookie-link">
                Cookie Policy
              </Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="footer-cookie-settings-button"
              >
                Cookie Settings
              </button>
              <Link to="/website-disclaimer" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-disclaimer-link">
                Website Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
